import { Button, Col, Form, Input, Row, Select } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProps } from 'antd/es/form/Form';

// services
import NotificationService from 'core/services/notification';
import { onGetAllRegions } from 'modules/regions/services/regions';
import { CreateCityPayload, onCreateCity } from 'modules/cities/services/cities';

// components
import BackButton from 'components/backButton/backButton';
import { useTranslation } from 'react-i18next';

type FieldType = {
	'name[en]': string;
	'name[ar]': string;
	active: 1;
	region_id: number;
};

function CreateCityPage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [regions, setRegions] = useState<{ label: string; value: number }[]>([]);
	const [createForm] = Form.useForm();

	useEffect(() => {
		getRegions();
	}, []);

	const getRegions = async () => {
		const response = await onGetAllRegions();
		setRegions(
			response.data.data.map((el: any) => {
				return { label: el.name, value: el.id };
			}),
		);
		setIsLoading(false);
	};

	const onSubmit: FormProps<FieldType>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const createCityPayload: CreateCityPayload = {
				name: {
					ar: values['name[ar]'],
					en: values['name[en]'],
				},
				active: 1,
				region_id: values.region_id,
			};

			await onCreateCity(createCityPayload);
			NotificationService.success(t('COMMON.SUCCESS'), t('CITIES.CREATED_SUCCESS'));
			navigate('/cities');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.OPS'), t('CITIES.CREATE_ERROR'));
		}
	};

	return (
		<>
			<div className="pageHeader">
				<BackButton />
				<h3>{t('CITIES.CREATE_TITLE')}</h3>
			</div>

			<div className="sectionContainer">
				<h5 style={{ marginBottom: 20 }}>{t('CITIES.DETAILS')}</h5>

				<Form name="createForm" form={createForm} onFinish={onSubmit}>
					<h6>{t('CITIES.INFORMATION')}</h6>
					<Row gutter={[20, 20]}>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="name[en]" rules={[{ required: true, message: t('CITIES.VALIDATION.NAME_EN') }]}>
								<Input size="large" placeholder={t('CITIES.PLACEHOLDER.NAME_EN')} />
							</Form.Item>
						</Col>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="name[ar]" rules={[{ required: true, message: t('CITIES.VALIDATION.NAME_AR') }]}>
								<Input size="large" placeholder={t('CITIES.PLACEHOLDER.NAME_AR')} />
							</Form.Item>
						</Col>
					</Row>

					<h6>{t('CITIES.REGION')}</h6>
					<Row gutter={[20, 20]}>
						<Col span={24} md={12}>
							<Form.Item<FieldType> name="region_id" rules={[{ required: true, message: t('CITIES.VALIDATION.REGION') }]}>
								<Select
									style={{ width: '100%' }}
									size="large"
									options={regions}
									loading={isLoading}
									showSearch
									filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
								/>
							</Form.Item>
						</Col>
					</Row>
				</Form>

				<Form.Item>
					<Button
						size="middle"
						type="primary"
						htmlType="submit"
						form="createForm"
						loading={isSubmitting}
						style={{ marginTop: 20, display: 'flex' }}>
						{t('COMMON.ADD')}
					</Button>
				</Form.Item>
			</div>
		</>
	);
}

export default CreateCityPage;
